import { getAnalytics, logEvent, isSupported, setUserProperties } from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';
import { getFirebaseConfig } from '../firebase-config.ts';
import { initializeApp, getApps } from 'firebase/app';

// Event ownership - FRONTEND ONLY EVENTS
const FRONTEND_EVENTS = [
  'page_view',
  'product_view', 
  'add_to_cart',
  'remove_from_cart',
  'begin_checkout',
  'cart_view',
  'search',
  'search_cleared',
  'filter_applied',
  'age_verification',
  'store_selected',
  'navigation_click',
  'menu_interaction',
  'purchase',
  'session_start',
  'session_end',
  'time_on_page',
  'scroll_depth',
  'bounce_rate',
  'returning_visitor',
  'performance_metric',
  'page_load_time',
  'api_response_time',
  'user_property_set',
  'custom_dimension_updated',
  'javascript_error',
  'form_validation_error',
  // Enhanced GA4 ecommerce events
  'view_item',
  'view_search_results', 
  'view_promotion',
  'select_item',
  'out_of_stock_product_view',
  'session_replay_marker',
  'category_viewing_time',
  'frustration_signal',
  'select_promotion',
  'login',
  'sign_up',
  'age_verification_completed',
  'checkout_progress',
  'product_engagement',
  'view_item_list'
] as const;

type FrontendEvent = typeof FRONTEND_EVENTS[number];

interface BaseEventProperties {
  page_path?: string;
  store_id?: string;
  timestamp?: number;
  session_id?: string;
  user_agent?: string;
  // Platform identification for mobile vs web differentiation
  app_platform?: 'web' | 'mobile' | 'desktop';
  app_type?: 'webapp' | 'ios' | 'android' | 'desktop';
  client_type?: 'browser' | 'mobile_app' | 'desktop_app';
  client_name?: string;
  app_version?: string;
}

interface PageViewProperties extends BaseEventProperties {
  page_title: string;
  referrer?: string;
}

interface ProductViewProperties extends BaseEventProperties {
  product_id: string;
  product_name: string;
  category: string;
  subcategory?: string;
  brand?: string;
  price?: number;
  thc_content?: number;
  cbd_content?: number;
}

interface CartEventProperties extends BaseEventProperties {
  product_id: string;
  product_name: string;
  quantity: number;
  variant_id?: string;
  price?: number;
  cart_total_items?: number;
  cart_total_value?: number;
}

interface SearchProperties extends BaseEventProperties {
  search_term: string;
  results_count: number;
}

interface FilterProperties extends BaseEventProperties {
  filter_type: string;
  filter_value: string;
  results_count: number;
}

interface StoreSelectedProperties extends BaseEventProperties {
  store_name: string;
  store_state: string;
  selection_method: 'dropdown' | 'map' | 'direct_link';
}

interface NavigationProperties extends BaseEventProperties {
  link_text: string;
  destination: string;
  source_page: string;
}

interface PurchaseProperties extends BaseEventProperties {
  transaction_id: string;
  value: number;
  currency: string;
  tax?: number;
  shipping?: number;
  items: Array<{
    item_id: string;
    item_name: string;
    item_category?: string;
    item_brand?: string;
    quantity: number;
    price: number;
    item_variant?: string;
  }>;
  coupon?: string;
  affiliation?: string;
  // Custom Dutchie tracking properties
  dutchie_order_id?: string;
  dutchie_order_number?: string;
  order_source?: string;
}

interface SessionProperties extends BaseEventProperties {
  session_duration?: number; // in milliseconds
  engagement_time?: number;
  pages_viewed?: number;
  events_triggered?: number;
}

interface TimeOnPageProperties extends BaseEventProperties {
  time_spent: number; // in seconds
  page_title: string;
  engaged_time?: number; // active time vs total time
}

interface ScrollDepthProperties extends BaseEventProperties {
  page_title: string;
  scroll_depth_percentage: number; // 0-100
  max_scroll_reached: number; // pixels from top
  page_height: number; // total page height
  viewport_height: number; // visible area height
}

interface BounceRateProperties extends BaseEventProperties {
  page_title: string;
  time_on_page: number; // seconds before bounce
  interactions_count: number; // clicks, scrolls, etc.
  bounced: boolean; // true if user left without meaningful interaction
}

interface ReturningVisitorProperties extends BaseEventProperties {
  visitor_type: 'new' | 'returning';
  previous_visit_count: number;
  days_since_last_visit?: number;
  total_session_count: number;
}

interface PerformanceMetricProperties extends BaseEventProperties {
  metric_name: string;
  metric_value: number; // in milliseconds
  metric_type: 'timing' | 'counter' | 'gauge';
  category?: string; // 'page_load', 'api', 'user_interaction', etc.
  custom_attributes?: Record<string, string>;
}

interface PageLoadTimeProperties extends BaseEventProperties {
  page_title: string;
  load_time: number; // total page load time in ms
  dom_content_loaded: number; // DOMContentLoaded time in ms
  first_contentful_paint?: number; // FCP in ms
  largest_contentful_paint?: number; // LCP in ms
  first_input_delay?: number; // FID in ms
  cumulative_layout_shift?: number; // CLS score
}

interface ApiResponseTimeProperties extends BaseEventProperties {
  endpoint: string;
  method: string;
  response_time: number; // in milliseconds
  status_code: number;
  success: boolean;
  error_message?: string;
}

interface UserPropertySetProperties extends BaseEventProperties {
  property_name: string;
  property_value: string;
  previous_value?: string;
  property_category?: 'demographic' | 'behavioral' | 'custom' | 'system';
}

interface CustomDimensionProperties extends BaseEventProperties {
  dimension_name: string;
  dimension_value: string;
  dimension_index?: number; // for GA4 custom dimensions
  scope?: 'user' | 'session' | 'event';
}

interface JavaScriptErrorProperties extends BaseEventProperties {
  error_message: string;
  error_stack?: string;
  error_type: string; // Error, TypeError, ReferenceError, etc.
  file_name?: string;
  line_number?: number;
  column_number?: number;
  user_agent: string;
  url: string;
  component_name?: string; // React component where error occurred
}


interface FormValidationErrorProperties extends BaseEventProperties {
  form_name: string;
  field_name: string;
  error_message: string;
  field_value?: string; // sanitized
  validation_rule: string; // required, email, minLength, etc.
  attempt_count?: number;
}

type EventProperties = 
  | PageViewProperties
  | ProductViewProperties 
  | CartEventProperties
  | SearchProperties
  | FilterProperties
  | StoreSelectedProperties
  | NavigationProperties
  | PurchaseProperties
  | SessionProperties
  | TimeOnPageProperties
  | ScrollDepthProperties
  | BounceRateProperties
  | ReturningVisitorProperties
  | PerformanceMetricProperties
  | PageLoadTimeProperties
  | ApiResponseTimeProperties
  | UserPropertySetProperties
  | CustomDimensionProperties
  | JavaScriptErrorProperties
  | ApiErrorProperties
  | FormValidationErrorProperties
  | BaseEventProperties;

class AnalyticsService {
  private analytics: any = null;
  private performance: any = null;
  private isInitialized = false;
  private isProductionEnv = false;
  private sessionId: string;
  private sessionStartTime: number;
  private pageStartTime: number;
  private pagesViewed: number = 0;
  private eventsTriggered: number = 0;
  private lastActivityTime: number;
  
  // User engagement tracking
  private maxScrollDepth: number = 0;
  private interactionCount: number = 0;
  private scrollDepthCheckpoints: number[] = [25, 50, 75, 90, 100];
  private reportedScrollDepths: Set<number> = new Set();
  private isFirstSession: boolean = false;
  
  // User properties and custom dimensions
  private userProperties: Record<string, string> = {};
  private customDimensions: Record<string, string> = {};
  
  // Error tracking
  private errorCount: number = 0;
  private errorHistory: Array<{timestamp: number, type: string, message: string}> = [];

  /**
   * Platform identification utility for mobile vs web analytics differentiation
   * 
   * This ensures all web app events are clearly identified as coming from the browser-based
   * web application, making it easy to segment and compare with future mobile app analytics.
   * 
   * When you add mobile apps, they should use:
   * - iOS app: { app_platform: 'mobile', app_type: 'ios', client_type: 'mobile_app', client_name: 'sweetspot-ios-app' }
   * - Android app: { app_platform: 'mobile', app_type: 'android', client_type: 'mobile_app', client_name: 'sweetspot-android-app' }
   */
  private getPlatformInfo() {
    return {
      app_platform: 'web' as const,
      app_type: 'webapp' as const,
      client_type: 'browser' as const,
      app_version: process.env.REACT_APP_VERSION || '3.1.0',
      client_name: 'sweetspot-web-app'
    };
  }

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.pageStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.initialize();
    this.startSessionTracking();
    this.initializeUserEngagement();
    this.initializePerformanceMonitoring();
    this.initializeUserProperties();
    this.initializeErrorTracking();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initialize() {
    try {
      // Check environment and initialize analytics accordingly
      this.isProductionEnv = process.env.REACT_APP_ENV === 'production';
      const currentEnv = process.env.REACT_APP_ENV || 'development';
      
      // Check if analytics is supported in this environment
      const supported = await isSupported();
      if (!supported) {
        return;
      }

      // Initialize Firebase app and analytics with appropriate config
      const firebaseConfig = getFirebaseConfig(); // This will get QA config for dev
      const existingApps = getApps();
      const app = existingApps.length > 0 ? existingApps[0] : initializeApp(firebaseConfig);
      this.analytics = getAnalytics(app);

      // Initialize Firebase Performance Monitoring
      this.performance = getPerformance(app);

      this.isInitialized = true;
    } catch (error) {
      // Analytics initialization failed silently
    }
  }

  private validateEventType(eventName: string): boolean {
    if (!FRONTEND_EVENTS.includes(eventName as FrontendEvent)) {
      return false;
    }
    return true;
  }

  private enrichProperties(properties: EventProperties): EventProperties & BaseEventProperties {
    return {
      ...properties,
      timestamp: Date.now(),
      session_id: this.sessionId,
      page_path: properties.page_path || window.location.pathname,
      user_agent: navigator.userAgent,
      // Platform identification for differentiating web vs mobile app
      ...this.getPlatformInfo()
    };
  }

  // Enhanced GA4 view_item_list with positioning and context
  public trackViewItemList(properties: {
    item_list_name: string;
    item_list_id: string;
    items: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      item_brand?: string;
      price: number;
      index: number; // Position in list (0-based)
      quantity?: number;
      item_variant?: string;
    }>;
    promotion_id?: string;
    promotion_name?: string;
    list_type?: 'search' | 'category' | 'deals' | 'trending' | 'recommended';
  }) {
    this.trackEvent('view_item_list', properties);
  }

  // Promotion tracking events for marketing analysis
  public trackViewPromotion(properties: {
    creative_name?: string;
    creative_slot?: string;
    promotion_id: string;
    promotion_name: string;
    items?: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      price: number;
    }>;
  }) {
    this.trackEvent('view_promotion', properties);
  }

  public trackSelectPromotion(properties: {
    creative_name?: string;
    creative_slot?: string;
    promotion_id: string;
    promotion_name: string;
  }) {
    this.trackEvent('select_promotion', properties);
  }

  // Authentication and user lifecycle events
  public trackLogin(properties: {
    method: 'email' | 'google' | 'facebook' | 'guest_checkout';
  }) {
    this.trackEvent('login', properties);
  }

  public trackSignUp(properties: {
    method: 'email' | 'google' | 'facebook';
    user_type: 'medical' | 'recreational';
  }) {
    this.trackEvent('sign_up', properties);
  }

  public trackAgeVerification(properties: {
    verification_method: string;
    compliance_state: string;
    user_age_range?: string;
  }) {
    this.trackEvent('age_verification_completed', properties);
  }

  // Enhanced checkout step tracking
  public trackCheckoutProgress(properties: {
    checkout_step: number;
    checkout_option?: string;
    step_name: 'cart' | 'customer_info' | 'payment' | 'review';
    currency?: string;
    value?: number;
    items?: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      price: number;
      quantity: number;
    }>;
  }) {
    this.trackEvent('checkout_progress', properties);
  }

  public trackEvent(eventName: string, properties: EventProperties = {}) {
    // console.log(`[ANALYTICS] 🚀 trackEvent called: ${eventName}`);
    
    // Validate event ownership
    if (!this.validateEventType(eventName)) {
      // console.error(`[ANALYTICS] ❌ Event validation failed for: ${eventName}`);
      return;
    }

    const enrichedProperties = this.enrichProperties(properties);
    
    // console.log(`[ANALYTICS] ✅ Event validation passed for: ${eventName}`);
    // console.log(`[ANALYTICS] 📝 Enriched properties:`, JSON.stringify(enrichedProperties, null, 2));
    
    // Count events for session tracking (exclude session events themselves)
    if (!['session_start', 'session_end'].includes(eventName)) {
      this.eventsTriggered++;
    }

    // console.log(`[ANALYTICS] 🔍 Analytics state check:`);
    // console.log(`[ANALYTICS] - isInitialized: ${this.isInitialized}`);
    // console.log(`[ANALYTICS] - analytics exists: ${!!this.analytics}`);
    // console.log(`[ANALYTICS] - isProductionEnv: ${this.isProductionEnv}`);

    // Send to Firebase Analytics (production sends to prod, development sends to QA)
    if (this.isInitialized && this.analytics) {
      try {
        // console.log(`[ANALYTICS] 📤 Sending ${eventName} to Firebase...`);
        logEvent(this.analytics, eventName, enrichedProperties);
        
        if (this.isProductionEnv) {
          // console.log(`[ANALYTICS] 📊 [PROD] Event sent successfully: ${eventName}`);
        } else {
          // console.log(`[ANALYTICS] 🧪 [DEV] Event sent to QA project: ${eventName}`, enrichedProperties);
        }
        
        // Special logging for purchase events
        if (eventName === 'purchase') {
          // console.log(`[ANALYTICS] 💰 PURCHASE EVENT DETAILS:`);
          // console.log(`[ANALYTICS] - Transaction ID: ${enrichedProperties.transaction_id}`);
          // console.log(`[ANALYTICS] - Value: $${enrichedProperties.value}`);
          // console.log(`[ANALYTICS] - Currency: ${enrichedProperties.currency}`);
          // console.log(`[ANALYTICS] - Items Count: ${enrichedProperties.items?.length || 0}`);
          // console.log(`[ANALYTICS] - Dutchie Order ID: ${enrichedProperties.dutchie_order_id || 'N/A'}`);
        }
        
      } catch (error) {
        // console.error(`[ANALYTICS] ❌ Failed to send ${eventName}:`, error);
        // console.error(`[ANALYTICS] ❌ Error details:`, JSON.stringify(error, null, 2));
      }
    } else {
      // console.warn(`[ANALYTICS] ⚠️ Cannot send event - Analytics not ready:`);
      // console.warn(`[ANALYTICS] - Event: ${eventName}`);
      // console.warn(`[ANALYTICS] - isInitialized: ${this.isInitialized}`);
      // console.warn(`[ANALYTICS] - analytics object: ${this.analytics ? 'EXISTS' : 'NULL'}`);
    }
  }

  // Convenience methods for common events
  public trackPageView(properties: PageViewProperties) {
    this.trackEvent('page_view', properties);
  }

  public trackProductView(properties: ProductViewProperties) {
    this.trackEvent('product_view', properties);
  }

  // Enhanced GA4 ecommerce event for individual product viewing
  public trackViewItem(properties: {
    currency?: string;
    value: number;
    items: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      item_category2?: string;
      item_brand?: string;
      item_price: number;
      item_quantity?: number;
      item_variant?: string;
      location_id?: string;
    }>;
  }) {
    this.trackEvent('view_item', {
      currency: properties.currency || 'USD',
      value: properties.value,
      items: properties.items
    });
  }

  public trackAddToCart(properties: CartEventProperties) {
    this.trackEvent('add_to_cart', properties);
  }

  public trackRemoveFromCart(properties: CartEventProperties) {
    this.trackEvent('remove_from_cart', properties);
  }

  public trackBeginCheckout(properties: BaseEventProperties & { 
    cart_total_items: number;
    cart_total_value: number;
    checkout_method: string;
  }) {
    this.trackEvent('begin_checkout', properties);
  }

  public trackSearch(properties: SearchProperties) {
    this.trackEvent('search', properties);
  }

  // Enhanced search results tracking for search optimization
  public trackViewSearchResults(properties: {
    search_term: string;
    search_results_count: number;
    search_filters_applied?: string[];
    search_sort_method?: string;
    search_refinements?: string[];
    search_category?: string;
    search_duration_ms?: number;
  }) {
    this.trackEvent('view_search_results', properties);
  }

  public trackFilterApplied(properties: FilterProperties) {
    this.trackEvent('filter_applied', properties);
  }

  public trackStoreSelected(properties: StoreSelectedProperties) {
    this.trackEvent('store_selected', properties);
  }

  public trackNavigation(properties: NavigationProperties) {
    this.trackEvent('navigation_click', properties);
  }

  public trackPurchase(properties: PurchaseProperties) {
    this.trackEvent('purchase', properties);
  }

  public trackScrollDepth(properties: ScrollDepthProperties) {
    this.trackEvent('scroll_depth', properties);
  }

  public trackBounceRate(properties: BounceRateProperties) {
    this.trackEvent('bounce_rate', properties);
  }

  public trackReturningVisitor(properties: ReturningVisitorProperties) {
    this.trackEvent('returning_visitor', properties);
  }

  public trackPerformanceMetric(properties: PerformanceMetricProperties) {
    this.trackEvent('performance_metric', properties);
  }

  public trackPageLoadTime(properties: PageLoadTimeProperties) {
    this.trackEvent('page_load_time', properties);
  }

  public trackApiResponseTime(properties: ApiResponseTimeProperties) {
    this.trackEvent('api_response_time', properties);
  }

  public trackUserPropertySet(properties: UserPropertySetProperties) {
    this.trackEvent('user_property_set', properties);
  }

  public trackCustomDimension(properties: CustomDimensionProperties) {
    this.trackEvent('custom_dimension_updated', properties);
  }

  public trackJavaScriptError(properties: JavaScriptErrorProperties) {
    this.trackEvent('javascript_error', properties);
  }


  public trackFormValidationError(properties: FormValidationErrorProperties) {
    this.trackEvent('form_validation_error', properties);
  }

  // Session tracking methods
  private startSessionTracking() {
    this.trackEvent('session_start', {
      session_duration: 0,
      engagement_time: 0,
      pages_viewed: 0,
      events_triggered: 0
    });

    // Track user activity for engagement time
    ['click', 'keydown', 'scroll', 'mousemove'].forEach(event => {
      document.addEventListener(event, () => {
        this.lastActivityTime = Date.now();
      }, { passive: true });
    });

    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    // Track session end on page visibility change (mobile/tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.endSession();
      }
    });
  }

  private endSession() {
    const sessionDuration = Date.now() - this.sessionStartTime;
    const engagementTime = this.lastActivityTime - this.sessionStartTime;
    
    this.trackEvent('session_end', {
      session_duration: sessionDuration,
      engagement_time: Math.min(engagementTime, sessionDuration),
      pages_viewed: this.pagesViewed,
      events_triggered: this.eventsTriggered
    });
  }

  public trackTimeOnPage(pagePath: string, pageTitle: string) {
    const timeSpent = Math.round((Date.now() - this.pageStartTime) / 1000); // Convert to seconds
    const engagedTime = Math.round((this.lastActivityTime - this.pageStartTime) / 1000);
    
    if (timeSpent > 1) { // Only track if user spent more than 1 second
      this.trackEvent('time_on_page', {
        page_path: pagePath,
        page_title: pageTitle,
        time_spent: timeSpent,
        engaged_time: Math.min(engagedTime, timeSpent)
      });
    }
  }

  public onPageChange() {
    // Track time on previous page before changing
    if (this.pagesViewed > 0) {
      this.trackTimeOnPage(window.location.pathname, document.title);
      this.checkBounceRate();
    }
    
    this.pageStartTime = Date.now();
    this.pagesViewed++;
    this.resetPageEngagement();
  }

  // User Engagement Tracking Methods
  private initializeUserEngagement() {
    this.checkReturningVisitor();
    this.setupScrollTracking();
    this.setupInteractionTracking();
  }

  private checkReturningVisitor() {
    const visitHistory = localStorage.getItem('sweetspot_visit_history');
    let history: any = {};
    
    try {
      history = visitHistory ? JSON.parse(visitHistory) : {};
    } catch (e) {
      history = {};
    }

    const now = Date.now();
    const lastVisit = history.lastVisit;
    const visitCount = history.visitCount || 0;
    const sessionCount = history.sessionCount || 0;

    this.isFirstSession = !lastVisit;
    const daysSinceLastVisit = lastVisit ? Math.floor((now - lastVisit) / (1000 * 60 * 60 * 24)) : undefined;

    // Track returning visitor
    this.trackEvent('returning_visitor', {
      visitor_type: this.isFirstSession ? 'new' : 'returning',
      previous_visit_count: visitCount,
      days_since_last_visit: daysSinceLastVisit,
      total_session_count: sessionCount + 1
    });

    // Update visit history
    const updatedHistory = {
      lastVisit: now,
      visitCount: visitCount + (this.isFirstSession ? 1 : 0),
      sessionCount: sessionCount + 1
    };
    localStorage.setItem('sweetspot_visit_history', JSON.stringify(updatedHistory));
  }

  private setupScrollTracking() {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateScrollDepth();
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  private updateScrollDepth() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );
    const viewportHeight = window.innerHeight;
    
    // Calculate scroll percentage
    const scrollableHeight = documentHeight - viewportHeight;
    const scrollPercentage = scrollableHeight > 0 ? Math.min(100, Math.round((scrollTop / scrollableHeight) * 100)) : 100;
    
    // Update max scroll depth
    this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollPercentage);

    // Check and report scroll depth milestones
    for (const checkpoint of this.scrollDepthCheckpoints) {
      if (scrollPercentage >= checkpoint && !this.reportedScrollDepths.has(checkpoint)) {
        this.reportedScrollDepths.add(checkpoint);
        this.trackEvent('scroll_depth', {
          page_title: document.title,
          scroll_depth_percentage: checkpoint,
          max_scroll_reached: scrollTop,
          page_height: documentHeight,
          viewport_height: viewportHeight
        });
      }
    }
  }

  private setupInteractionTracking() {
    const interactions = ['click', 'keydown', 'touchstart', 'mousemove', 'scroll'];
    
    interactions.forEach(eventType => {
      document.addEventListener(eventType, () => {
        this.interactionCount++;
        this.lastActivityTime = Date.now();
      }, { passive: true });
    });
  }

  private checkBounceRate() {
    const timeOnPage = Math.round((Date.now() - this.pageStartTime) / 1000);
    const bounced = timeOnPage < 15 || this.interactionCount < 2; // Less than 15 seconds or minimal interactions
    
    this.trackEvent('bounce_rate', {
      page_title: document.title,
      time_on_page: timeOnPage,
      interactions_count: this.interactionCount,
      bounced: bounced
    });
  }

  private resetPageEngagement() {
    this.maxScrollDepth = 0;
    this.interactionCount = 0;
    this.reportedScrollDepths.clear();
  }

  // Performance Monitoring Methods
  private initializePerformanceMonitoring() {
    this.setupPageLoadTracking();
    this.setupWebVitalsTracking();
    this.patchFetchForApiTracking();
  }

  private setupPageLoadTracking() {
    // Track page load performance when window loads
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.measurePageLoadPerformance();
      }, 100); // Small delay to ensure all measurements are available
    });

    // Also track when DOM content is loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.measureDOMContentLoaded();
      });
    } else {
      // Document already loaded
      setTimeout(() => this.measureDOMContentLoaded(), 0);
    }
  }

  private measurePageLoadPerformance() {
    if (!window.performance || !window.performance.timing) {
      return;
    }

    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;

    if (loadTime > 0) {
      this.trackPageLoadTime({
        page_title: document.title,
        load_time: loadTime,
        dom_content_loaded: domContentLoaded
      });

      // Also create a custom trace for Firebase Performance
      if (this.performance) {
        const pageTrace = trace(this.performance, 'page_load');
        pageTrace.putAttribute('page_path', window.location.pathname);
        pageTrace.putAttribute('page_title', document.title);
        pageTrace.putMetric('load_time_ms', loadTime);
        pageTrace.putMetric('dom_content_loaded_ms', domContentLoaded);
        // Note: traces are automatically submitted by Firebase Performance
      }
    }
  }

  private measureDOMContentLoaded() {
    if (!window.performance || !window.performance.timing) {
      return;
    }

    const timing = window.performance.timing;
    const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;

    if (domContentLoaded > 0) {
      this.trackPerformanceMetric({
        metric_name: 'dom_content_loaded',
        metric_value: domContentLoaded,
        metric_type: 'timing',
        category: 'page_load'
      });
    }
  }

  private setupWebVitalsTracking() {
    // Track Core Web Vitals using PerformanceObserver
    try {
      // First Contentful Paint (FCP)
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              this.trackPerformanceMetric({
                metric_name: 'first_contentful_paint',
                metric_value: Math.round(entry.startTime),
                metric_type: 'timing',
                category: 'web_vitals'
              });
            }
          }
        });
        observer.observe({ entryTypes: ['paint'] });

        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            this.trackPerformanceMetric({
              metric_name: 'largest_contentful_paint',
              metric_value: Math.round(lastEntry.startTime),
              metric_type: 'timing',
              category: 'web_vitals'
            });
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.processingStart && entry.startTime) {
              const fid = entry.processingStart - entry.startTime;
              this.trackPerformanceMetric({
                metric_name: 'first_input_delay',
                metric_value: Math.round(fid),
                metric_type: 'timing',
                category: 'web_vitals'
              });
            }
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      }
    } catch (error) {
      // Web Vitals tracking setup failed silently
    }
  }


  // Public method to create custom performance traces
  public createTrace(traceName: string): any {
    if (this.performance) {
      return trace(this.performance, traceName);
    }
    return null;
  }

  // User Properties and Custom Dimensions Methods
  private initializeUserProperties() {
    this.setSystemUserProperties();
    this.setBehavioralUserProperties();
    this.loadStoredUserProperties();
  }

  private setSystemUserProperties() {
    const systemProperties: Record<string, string> = {
      user_agent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      first_visit_timestamp: this.isFirstSession ? Date.now().toString() : (localStorage.getItem('sweetspot_first_visit') || Date.now().toString())
    };

    // Store first visit timestamp
    if (this.isFirstSession) {
      localStorage.setItem('sweetspot_first_visit', systemProperties.first_visit_timestamp);
    }

    this.setUserProperties(systemProperties, 'system');
  }

  private setBehavioralUserProperties() {
    const visitHistory = this.getVisitHistory();
    const behavioralProperties: Record<string, string> = {
      visitor_type: this.isFirstSession ? 'new' : 'returning',
      total_sessions: (visitHistory.sessionCount || 1).toString(),
      total_visits: (visitHistory.visitCount || 1).toString(),
      preferred_device: this.getDeviceType(),
      session_count_bucket: this.getSessionCountBucket(visitHistory.sessionCount || 1)
    };

    if (!this.isFirstSession && visitHistory.lastVisit) {
      const daysSinceLastVisit = Math.floor((Date.now() - visitHistory.lastVisit) / (1000 * 60 * 60 * 24));
      behavioralProperties.days_since_last_visit = daysSinceLastVisit.toString();
      behavioralProperties.recency_bucket = this.getRecencyBucket(daysSinceLastVisit);
    }

    this.setUserProperties(behavioralProperties, 'behavioral');
  }

  private loadStoredUserProperties() {
    try {
      const storedProps = localStorage.getItem('sweetspot_user_properties');
      if (storedProps) {
        const parsedProps = JSON.parse(storedProps);
        Object.assign(this.userProperties, parsedProps);
      }
    } catch (error) {
      // Failed to load stored user properties silently
    }
  }

  private getVisitHistory() {
    try {
      const visitHistory = localStorage.getItem('sweetspot_visit_history');
      return visitHistory ? JSON.parse(visitHistory) : {};
    } catch (e) {
      return {};
    }
  }

  private getDeviceType(): string {
    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      return 'tablet';
    }
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
      return 'mobile';
    }
    return 'desktop';
  }

  private getSessionCountBucket(sessionCount: number): string {
    if (sessionCount === 1) return '1_session';
    if (sessionCount <= 3) return '2-3_sessions';
    if (sessionCount <= 10) return '4-10_sessions';
    if (sessionCount <= 25) return '11-25_sessions';
    return '25+_sessions';
  }

  private getRecencyBucket(daysSinceLastVisit: number): string {
    if (daysSinceLastVisit === 0) return 'same_day';
    if (daysSinceLastVisit <= 1) return '1_day';
    if (daysSinceLastVisit <= 7) return '1_week';
    if (daysSinceLastVisit <= 30) return '1_month';
    if (daysSinceLastVisit <= 90) return '3_months';
    return '3+_months';
  }

  // Public methods for setting user properties and custom dimensions
  public setUserProperties(properties: Record<string, string>, category: 'demographic' | 'behavioral' | 'custom' | 'system' = 'custom') {
    Object.entries(properties).forEach(([key, value]) => {
      const previousValue = this.userProperties[key];
      this.userProperties[key] = value;

      // Track the user property change
      this.trackUserPropertySet({
        property_name: key,
        property_value: value,
        previous_value: previousValue,
        property_category: category
      });
    });

    // Send to Firebase Analytics
    if (this.isInitialized && this.analytics) {
      try {
        setUserProperties(this.analytics, properties);
      } catch (error) {
        // Failed to set user properties silently
      }
    }

    // Store user properties locally
    this.storeUserProperties();
  }

  public setCustomDimension(dimensionName: string, dimensionValue: string, scope: 'user' | 'session' | 'event' = 'user') {
    this.customDimensions[dimensionName] = dimensionValue;

    this.trackCustomDimension({
      dimension_name: dimensionName,
      dimension_value: dimensionValue,
      scope: scope
    });

    // For session and user scoped dimensions, add to all subsequent events
    if (scope === 'user' || scope === 'session') {
      this.userProperties[`custom_${dimensionName}`] = dimensionValue;
    }
  }

  private storeUserProperties() {
    try {
      localStorage.setItem('sweetspot_user_properties', JSON.stringify(this.userProperties));
    } catch (error) {
      // Failed to store user properties silently
    }
  }

  // Public getters
  public getUserProperties(): Record<string, string> {
    return { ...this.userProperties };
  }

  public getCustomDimensions(): Record<string, string> {
    return { ...this.customDimensions };
  }

  public getUserProperty(propertyName: string): string | undefined {
    return this.userProperties[propertyName];
  }

  // Error Tracking Methods
  private initializeErrorTracking() {
    this.setupGlobalErrorHandling();
    this.setupUnhandledRejectionHandling();
    this.patchConsoleError();
  }

  private setupGlobalErrorHandling() {
    window.addEventListener('error', (event) => {
      this.handleJavaScriptError({
        error_message: event.message || 'Unknown error',
        error_stack: event.error?.stack,
        error_type: event.error?.name || 'Error',
        file_name: event.filename,
        line_number: event.lineno,
        column_number: event.colno,
        user_agent: navigator.userAgent,
        url: window.location.href
      });
    });
  }

  private setupUnhandledRejectionHandling() {
    window.addEventListener('unhandledrejection', (event) => {
      this.handleJavaScriptError({
        error_message: event.reason?.message || event.reason?.toString() || 'Unhandled promise rejection',
        error_stack: event.reason?.stack,
        error_type: 'UnhandledPromiseRejection',
        user_agent: navigator.userAgent,
        url: window.location.href
      });
    });
  }

  private patchConsoleError() {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Track console errors that might indicate issues
      const errorMessage = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      // Only track errors that look like actual errors (not just console.error calls)
      if (args[0] instanceof Error || errorMessage.includes('Error:')) {
        this.handleJavaScriptError({
          error_message: errorMessage,
          error_stack: args[0]?.stack,
          error_type: 'ConsoleError',
          user_agent: navigator.userAgent,
          url: window.location.href
        });
      }
      
      // Call original console.error
      originalConsoleError.apply(console, args);
    };
  }

  private handleJavaScriptError(errorProps: Omit<JavaScriptErrorProperties, keyof BaseEventProperties>) {
    // Avoid infinite loops by checking error count
    if (this.errorCount > 10) {
      return;
    }

    this.errorCount++;
    
    // Add to error history
    this.errorHistory.push({
      timestamp: Date.now(),
      type: errorProps.error_type,
      message: errorProps.error_message
    });

    // Track the error
    this.trackJavaScriptError({
      ...errorProps,
      page_path: window.location.pathname,
      session_id: this.sessionId
    });
  }



  // Public method for tracking form validation errors
  public handleFormValidationError(formName: string, fieldName: string, errorMessage: string, validationRule: string, fieldValue?: string, attemptCount?: number) {
    // Sanitize field value - remove sensitive data
    const sanitizedValue = this.sanitizeFieldValue(fieldValue, fieldName);
    
    this.trackFormValidationError({
      form_name: formName,
      field_name: fieldName,
      error_message: errorMessage,
      field_value: sanitizedValue,
      validation_rule: validationRule,
      attempt_count: attemptCount,
      page_path: window.location.pathname,
      session_id: this.sessionId
    });
  }

  private sanitizeFieldValue(value?: string, fieldName?: string): string | undefined {
    if (!value) return undefined;
    
    // List of sensitive field patterns
    const sensitivePatterns = [
      /password/i,
      /ssn/i,
      /social.security/i,
      /credit.card/i,
      /card.number/i,
      /cvv/i,
      /security.code/i
    ];

    // Check if field name matches sensitive patterns
    const isSensitive = sensitivePatterns.some(pattern => 
      fieldName ? pattern.test(fieldName) : false
    );

    if (isSensitive) {
      return '[REDACTED]';
    }

    // Limit length and remove potential PII
    return value.length > 50 ? value.substring(0, 50) + '...' : value;
  }

  // Enhanced fetch patching to include error tracking
  private patchFetchForApiTracking() {
    const originalFetch = window.fetch;
    
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const startTime = Date.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      const method = init?.method || 'GET';

      try {
        const response = await originalFetch(input, init);
        const endTime = Date.now();
        const responseTime = endTime - startTime;

        // Only track API calls to our own backend
        if (url.includes('/api/') || url.includes('sweetspot')) {
          this.trackApiResponseTime({
            endpoint: url,
            method: method,
            response_time: responseTime,
            status_code: response.status,
            success: response.ok
          });


          // Create Firebase Performance trace for API calls
          if (this.performance) {
            const apiTrace = trace(this.performance, `api_${method.toLowerCase()}`);
            // Extract just the path from the URL for Firebase attribute (avoid full URL length limits)
            const urlPath = url.includes('/api/') ? url.substring(url.indexOf('/api/')) : new URL(url).pathname;
            apiTrace.putAttribute('endpoint', urlPath);
            apiTrace.putAttribute('method', method);
            apiTrace.putAttribute('status_code', response.status.toString());
            apiTrace.putMetric('response_time_ms', responseTime);
            // Trace is automatically submitted
          }
        }

        return response;
      } catch (error) {
        const endTime = Date.now();
        const responseTime = endTime - startTime;

        // Track failed API calls
        if (url.includes('/api/') || url.includes('sweetspot')) {
          const errorMessage = error instanceof Error ? error.message : 'Network error';
          
          this.trackApiResponseTime({
            endpoint: url,
            method: method,
            response_time: responseTime,
            status_code: 0,
            success: false,
            error_message: errorMessage
          });

        }

        throw error;
      }
    };
  }

  // Public methods for error statistics
  public getErrorCount(): number {
    return this.errorCount;
  }

  public getErrorHistory(): Array<{timestamp: number, type: string, message: string}> {
    return [...this.errorHistory];
  }

  public clearErrorHistory(): void {
    this.errorHistory = [];
    this.errorCount = 0;
  }


  // Get current session ID for debugging
  public getSessionId(): string {
    return this.sessionId;
  }

  // Check if running in production
  public isProduction(): boolean {
    return this.isProductionEnv;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;